import { useState } from 'react'
import { useAuth } from '../contexts/AuthContext'
import { useUserCheck } from '../hooks/api/useUserCheck'
import { usePermissions } from '../hooks/usePermissions'
import { useCompanyUsers } from '../hooks/api/useUsers'
import { useTeamInvitations, useCreateTeamInvitation, useResendTeamInvitation } from '../hooks/api/useTeamInvitations'
import { DashboardLayout } from '../components/layout/DashboardLayout'
import { TeamMembersEmptyState } from '../components/users/TeamMembersEmptyState'
import { TeamMembersList } from '../components/users/TeamMembersList'
import { InviteTeamMemberModal, InviteTeamMemberFormData } from '../components/users/InviteTeamMemberModal'

export const UsersPage = () => {
  const { user } = useAuth()
  const { data: userRecord, isLoading: userLoading, error: userError } = useUserCheck(user?.id || '')
  const { hasPermission } = usePermissions({ userRole: userRecord?.role ?? null })
  const [showInviteModal, setShowInviteModal] = useState(false)
  const [feedback, setFeedback] = useState<{ type: 'success' | 'error'; message: string } | null>(null)
  const [resendingId, setResendingId] = useState<string | null>(null)

  const companyId = userRecord?.company_id

  const { data: members = [], isLoading: membersLoading } = useCompanyUsers(companyId)
  const { data: invitations = [], isLoading: invitationsLoading } = useTeamInvitations(companyId)
  const createInvitationMutation = useCreateTeamInvitation()
  const resendInvitationMutation = useResendTeamInvitation()

  const canInvite = hasPermission('CREATE_USERS')

  const handleSendInvite = async (form: InviteTeamMemberFormData) => {
    if (!userRecord?.id || !companyId) {
      throw new Error('Missing company information for invitation')
    }

    await createInvitationMutation.mutateAsync({
      company_id: companyId,
      invited_by_user_id: userRecord.id,
      email: form.email,
      full_name: form.fullName,
      role: form.role,
      include_device_setup: form.includeDeviceSetup || false,
      device_os_type: form.deviceOsType,
      device_name: form.deviceName
    })

    setFeedback({ type: 'success', message: `Invitation sent to ${form.fullName || form.email}.` })
  }

  const handleResendInvite = async (invitationId: string) => {
    setResendingId(invitationId)
    setFeedback(null)

    try {
      await resendInvitationMutation.mutateAsync(invitationId)
      setFeedback({ type: 'success', message: 'Invitation email resent successfully.' })
    } catch (error) {
      console.error('UsersPage: failed to resend invitation', error)
      setFeedback({ type: 'error', message: 'Failed to resend invitation. Please try again.' })
    } finally {
      setResendingId(null)
    }
  }

  if (userLoading || membersLoading || invitationsLoading) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-gray-50">
        <div className="text-center">
          <div className="mx-auto mb-4 h-8 w-8 animate-spin rounded-full border-3 border-gray-400 border-t-transparent border-r-transparent" />
          <p className="text-gray-600">Loading team members...</p>
        </div>
      </div>
    )
  }

  if (userError || !userRecord) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-gray-50">
        <div className="max-w-md rounded-lg border border-red-200 bg-red-50 p-6 text-center">
          <p className="text-sm font-medium text-red-600">❌ User not found in database!</p>
          <p className="mt-2 text-xs text-red-500">Please create a new account to test the full flow.</p>
        </div>
      </div>
    )
  }

  const hasAnyData = members.length > 0 || invitations.length > 0

  return (
    <DashboardLayout
      userRole={userRecord.role}
      adminLevel={userRecord.admin_level}
      userName={userRecord.full_name || user?.email}
      userEmail={user?.email}
    >
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-semibold text-gray-900">Team members</h1>
            <p className="mt-1 text-sm text-gray-500">
              Invite and manage the people who can access your company workspace.
            </p>
          </div>

          {canInvite && (
            <button
              onClick={() => setShowInviteModal(true)}
              className="inline-flex items-center rounded-full bg-gray-900 px-5 py-3 text-sm font-medium text-white transition-colors hover:bg-gray-800"
            >
              Invite Team Member
            </button>
          )}
        </div>

        {feedback && (
          <div
            className={`rounded-2xl border px-4 py-3 text-sm ${
              feedback.type === 'success'
                ? 'border-green-200 bg-green-50 text-green-700'
                : 'border-red-200 bg-red-50 text-red-600'
            }`}
          >
            {feedback.message}
          </div>
        )}

        {hasAnyData ? (
          <TeamMembersList
            members={members}
            invitations={invitations}
            onResendInvite={handleResendInvite}
            resendingInvitationId={resendingId}
          />
        ) : (
          <TeamMembersEmptyState
            canInvite={canInvite}
            onInvite={() => setShowInviteModal(true)}
          />
        )}
      </div>

      <InviteTeamMemberModal
        isOpen={showInviteModal}
        onClose={() => setShowInviteModal(false)}
        onInvite={handleSendInvite}
        isSubmitting={createInvitationMutation.isPending}
      />
    </DashboardLayout>
  )
}
